import { useEffect, useState, type FunctionComponent } from "react";
import { Outlet } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";
import { useGetPostsAdminHttp } from "../../../../hooks/http/admin/posts/get-posts.admin.http";
import type { IPagination } from "../../../../types/models/pagination.models.types";
import { type IPost, PostStatus } from "../../../../types/models/post.models.types";
import PaginationComponent from "../../../../components/pagination.components";
import { CampaignPostAdminPartial } from "./campaign-post.admin.partials";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import { useToast } from "../../../../hooks/use-toast";
import usePostStore from "../../../../store/post.store";
import { handleHttpError } from "../../../../utils/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

interface IProps {
  initialStatus: PostStatus;
  tabs: Array<{
    status: PostStatus;
    title: string;
    flagged: boolean;
  }>;

  campaignId: string;
}

enum PostSortBy {
  CREATED_AT_NEWEST = "createdAtNewest",
  CREATED_AT_OLDEST = "createdAtOldest",
  LIKES = "likes",
  VIEWS = "views",
  COMMENTS = "comments",
  SHARES = "shares",
}

const isDraftStatus = (status: PostStatus) =>
  status === PostStatus.draft || status === PostStatus.vendorDraft;

const getDefaultSort = (status: PostStatus) =>
  isDraftStatus(status)
    ? {
        sortBy: PostSortBy.CREATED_AT_OLDEST,
        sortOrder: "asc" as const,
      }
    : {
        sortBy: PostSortBy.CREATED_AT_NEWEST,
        sortOrder: "desc" as const,
      };

const CampaignPostsAdminPartial: FunctionComponent<IProps> = ({
  tabs,
  initialStatus,
  campaignId,
}) => {
  const [status, setStatus] = useState<PostStatus>(initialStatus);
  const [sortBy, setSortBy] = useState<PostSortBy>(
    getDefaultSort(initialStatus).sortBy,
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    getDefaultSort(initialStatus).sortOrder,
  );
  const { fetching, execute: getPostsHttp } = useGetPostsAdminHttp();
  const [pageNo, setPageNo] = useState(1);
  const [paginationData, setPaginationData] = useState<IPagination>();
  const { toast } = useToast();
  const { setData, data: postStore } = usePostStore();

  async function getPosts(
    page: number,
    status: PostStatus,
    sortBy: PostSortBy,
    sortOrder: "asc" | "desc",
  ) {
    let apiSortBy: string;
    let apiSortOrder: "asc" | "desc" = sortOrder;

    switch (sortBy) {
      case PostSortBy.CREATED_AT_NEWEST:
        apiSortBy = "createdAt";
        apiSortOrder = "desc";
        break;
      case PostSortBy.CREATED_AT_OLDEST:
        apiSortBy = "createdAt";
        apiSortOrder = "asc";
        break;
      case PostSortBy.LIKES:
      case PostSortBy.VIEWS:
      case PostSortBy.COMMENTS:
      case PostSortBy.SHARES:
        apiSortBy = sortBy;
        apiSortOrder = "desc";
        break;
      default:
        apiSortBy = "createdAt";
        apiSortOrder = "desc";
    }

    const response = await getPostsHttp({
      body: null,
      queryParams: {
        status,
        campaignId,
        page,
        sortBy: apiSortBy,
        sortOrder: apiSortOrder,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    setData((prevData) => {
      const newPosts = response.data.posts.filter((post) => {
        return prevData.findIndex((p) => p.id === post.id) === -1;
      });

      return [...prevData, ...newPosts];
    });

    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setData([]);
    setPageNo(1);
  getPosts(1, status, sortBy, sortOrder);
  }, [campaignId]);

  return (
    <div className="flex flex-col gap-8 pt-8 w-full">
      <Tabs
        value={status}
        onValueChange={(value) => {
          const nextpage = 1;
          const nextSatus = value as PostStatus;

          setData([]);
          setStatus(nextSatus);
          const nextDefaultSort = getDefaultSort(nextSatus);
          setSortBy(nextDefaultSort.sortBy);
          setSortOrder(nextDefaultSort.sortOrder);
          getPosts(
            nextpage,
            nextSatus,
            nextDefaultSort.sortBy,
            nextDefaultSort.sortOrder,
          );
        }}
      >
        <div className="flex justify-between items-center w-full">
          <TabsList>
            {tabs.map((tab) => {
              return (
                <TabsTrigger
                  key={tab.status}
                  className={`border-0 border-b-2 px-4 py-2 ${
                    tab.status === status ? "" : "border-border"
                  }`}
                  value={tab.status}
                >
                  {tab.title}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="flex items-center gap-2">
            <Select
              value={sortBy}
              onValueChange={(value) => {
                const nextSortBy = value as PostSortBy;
                setData([]);
                setPageNo(1);
                setSortBy(nextSortBy);

                if (nextSortBy === PostSortBy.CREATED_AT_NEWEST) {
                  setSortOrder("desc");
                  getPosts(1, status, nextSortBy, "desc");
                } else if (nextSortBy === PostSortBy.CREATED_AT_OLDEST) {
                  setSortOrder("asc");
                  getPosts(1, status, nextSortBy, "asc");
                } else {
                  // metrics sorts always desc
                  setSortOrder("desc");
                  getPosts(1, status, nextSortBy, "desc");
                }
              }}
            >
              <SelectTrigger className="gap-3 bg-tertiarybuttonbg border-border rounded-lg w-fit">
                <span className="text-secondary">Sort by:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
              <SelectItem value={PostSortBy.CREATED_AT_OLDEST}>
                  Oldest
                </SelectItem>
                
                <div className="border-border border-t" />

                <SelectItem value={PostSortBy.CREATED_AT_NEWEST}>
                  Latest
                </SelectItem>
                

                {status === PostStatus.postApproved && (
                  <>
                    <div className="border-border border-t" />

                    <SelectItem value={PostSortBy.LIKES}>Likes</SelectItem>
                    <div className="border-border border-t" />

                    <SelectItem value={PostSortBy.VIEWS}>Views</SelectItem>
                    <div className="border-border border-t" />

                    <SelectItem value={PostSortBy.COMMENTS}>
                      Comments
                    </SelectItem>
                    <div className="border-border border-t" />

                    <SelectItem value={PostSortBy.SHARES}>Shares</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {tabs.map((tab) => {
          return (
            <TabsContent key={tab.status} value={tab.status} className="py-12">
              {fetching && postStore.length === 0 ? (
                <div className="flex justify-center items-center w-full h-96">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="gap-6 grid grid-cols-3">
                    {postStore.map((post: IPost) => (
                      <CampaignPostAdminPartial key={post.id} post={post} />
                    ))}
                  </div>

                  {paginationData && (
                    <PaginationComponent
                      fetching={fetching}
                      paginationData={paginationData}
                      onClick={() => {
                        const nextpage = pageNo + 1;
                        setPageNo(nextpage);
                        getPosts(nextpage, status, sortBy, sortOrder);
                      }}
                    />
                  )}
                  {postStore.length === 0 && (
                    <div className="flex justify-center items-center w-full h-96">
                      <p>No posts found </p>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
      <Outlet />
      {/* biome-ignore lint/a11y/useButtonType: <explanation> */}
      <button
        id="getPosts"
        onClick={() => {
          setData([]);
          getPosts(1, status, sortBy, sortOrder);
        }}
        className="hidden"
      />
    </div>
  );
};

export default CampaignPostsAdminPartial;
